import type { TopicConfig } from '@otter-ai/recall-client';
import {
  GeneralButtonActionEvent,
  Screen,
  trackEvent,
  UIElementID,
} from '@otter-ai/telemetry';
import { useCallback, useMemo } from 'react';
import { useWorkspace } from '@/shared/hooks/useWorkspace';
import { openDialog } from '@/shared/modals/openDialog';
import {
  CRM_APPS,
  type HubspotSyncedAccount,
  type TeamApp,
} from '../type/apps';
import { useGetAutofillConfigs } from './useGetAutofillConfigs';
import { useSyncedAccounts } from './useSyncedAccounts';
import { useTeamApps } from './useTeamApps';

export type HubspotSettingsHooks = {
  isLoading: boolean;
  /**
   * Whether the user has permissions to manage HubSpot from a team level.
   *
   * @example
   * ```tsx
   * const { canManageTeamHubspot } = useHubspotSettings();
   * return (
   *   <div>{
   *   canManageTeamHubspot ?
   *     'You can manage HubSpot' :
   *     'You cannot manage HubSpot'
   *   }</div>
   * );
   * ```
   */
  canManageTeamHubspot: boolean | null;
  /**
   * Open the HubSpot team manage modal.
   *
   * @example
   * ```tsx
   * const { openHubspotTeamManageModal } = useHubspotSettings();
   * const onClick = async () => {
   *   await openHubspotTeamManageModal();
   *   // The modal is now closed
   * };
   * return <button onClick={onClick}>Manage HubSpot</button>;
   * ```
   */
  openHubspotTeamManageModal: () => Promise<void>;
  /**
   * Whether the user is available to connect their HubSpot account to Otter
   */
  canConnectHubspotAccount: boolean | null;
  /**
   * Should show option to the admin to connect HubSpot to all workspace members
   */
  canAddAllHubspotAccounts: boolean | null;
  /**
   * Should show option to disconnect HubSpot
   */
  disableDisconnectHubspot: boolean | null;
  /**
   * Is the current user connected to HubSpot
   */
  isConnectToHubspot: boolean | null;
  /**
   * Should show buttons
   */
  canShowButtons: boolean | null;
  /**
   * Is the HubSpot integration enabled for the workspace.
   */
  hubspotIntegrationEnabled: boolean | null;
  /**
   * Can members link their HubSpot accounts to Otter.
   */
  hubspotMemberLinkEnabled: boolean | null;
  /**
   * Do all members share the same HubSpot account.
   */
  hubspotTeamLinkEnabled: boolean | null;
};

/**
 * Get a function to open the HubSpot manage modal along with
 * if the user has permissions to manage HubSpot.
 *
 * @example
 * ```tsx
 * function Example() {
 *   const { canManageTeamHubspot, openHubspotTeamManageModal } = useHubspotSettings();
 *   return (<div>
 *     {canManageTeamHubspot ? 'You can manage HubSpot' : 'You cannot manage HubSpot'}
 *     {
 *       canManageTeamHubspot &&
 *       <button onClick={openHubspotTeamManageModal}>Manage HubSpot</button>
 *     }
 *   </div>);
 * }
 * ```
 *
 */
export function useHubspotSettings(): HubspotSettingsHooks {
  const {
    workspace,
    adminStatus,
    isLoading: isWorkspaceLoading,
  } = useWorkspace();
  const {
    isLoading: isAutofillConfigsLoading,
    isAutofillConfigEnabled: isHubspotAutofillConfigEnabled,
    hasAutofillConfig: hasHubspotAutofillConfig,
  } = useGetAutofillConfigs({
    destinationService: 'hubspot',
  });
  const { accounts, isLoading: isConnectedAccountsLoading } = useSyncedAccounts(
    { service: CRM_APPS.HUBSPOT },
  );
  const hubspotAccount = accounts?.[0] as HubspotSyncedAccount | undefined;
  const { teamApps, isLoading: isAppsLoading } = useTeamApps();
  const hubspotApp = teamApps?.find((app) => app.name === 'hubspot');

  const isLoading =
    isWorkspaceLoading ||
    isAutofillConfigsLoading ||
    isConnectedAccountsLoading ||
    isAppsLoading;

  const canConnectHubspotAccount = hubspotApp?.connection_enabled ?? false;

  const isConnectToHubspot = Boolean(hubspotAccount);

  const canManageTeamHubspot: boolean | null = useMemo(() => {
    if (isLoading) {
      return null;
    }

    if (!workspace) {
      return false;
    }
    return Boolean(
      hasHubspotAutofillConfig &&
        isHubspotAutofillConfigEnabled &&
        hubspotAccount &&
        !hubspotAccount.is_member_link &&
        adminStatus?.isAdmin,
    );
  }, [
    hubspotAccount,
    hasHubspotAutofillConfig,
    isHubspotAutofillConfigEnabled,
    adminStatus?.isAdmin,
    isLoading,
    workspace,
  ]);

  const openHubspotTeamManageModal = useCallback(async () => {
    trackEvent(
      GeneralButtonActionEvent({
        Screen: Screen.apps,
        UIElementID: UIElementID.manageFieldsHubSpot,
      }),
    );

    await openDialog(async ({ done }) => {
      if (isLoading) {
        throw new ModalOpenBeforeLoadedError();
      }

      if (!canManageTeamHubspot) {
        throw new LacksPermissionToManageHubspotError();
      }

      if (!workspace) {
        throw new UnreachableError('A workspace was expected');
      }

      const { CrmManageDialogTeam } = await import(
        '../components/CrmManageDialogTeam'
      );
      return (
        <CrmManageDialogTeam
          workspaceId={workspace.id.toString()}
          insights={workspace.workspace_topics as TopicConfig[]}
          destinationService="hubspot"
          handleCloseCallback={done}
        />
      );
    });
  }, [workspace, isLoading, canManageTeamHubspot]);

  const canAddAllHubspotAccounts = isLoading
    ? null
    : Boolean(
        hubspotApp?.member_link_enabled &&
          hubspotAccount?.is_member_link &&
          hubspotTeamLinkEnabled(hubspotApp) &&
          Boolean(adminStatus?.isAdmin),
      );

  const disableDisconnectHubspot = isLoading
    ? null
    : !hubspotAccount?.is_member_link && !adminStatus?.isAdmin;

  const canShowButtons = isLoading
    ? null
    : !hubspotAccount ||
      Boolean(hubspotAccount?.is_member_link) ||
      Boolean(adminStatus?.isAdmin);

  const teamAppSettings = useMemo(() => {
    if (isAppsLoading) {
      return {
        hubspotIntegrationEnabled: null,
        hubspotMemberLinkEnabled: null,
        hubspotTeamLinkEnabled: null,
      };
    }

    if (!hubspotApp) {
      return {
        hubspotIntegrationEnabled: false,
        hubspotMemberLinkEnabled: false,
        hubspotTeamLinkEnabled: false,
      };
    }

    return {
      hubspotIntegrationEnabled: hubspotApp.connection_enabled ?? null,
      hubspotMemberLinkEnabled: hubspotApp.member_link_enabled ?? null,
      hubspotTeamLinkEnabled: hubspotApp.team_link_enabled ?? null,
    };
  }, [hubspotApp, isAppsLoading]);

  return {
    isLoading,
    openHubspotTeamManageModal,
    canConnectHubspotAccount,
    isConnectToHubspot,
    canManageTeamHubspot,
    canAddAllHubspotAccounts,
    disableDisconnectHubspot,
    canShowButtons,
    ...teamAppSettings,
  };
}

function hubspotTeamLinkEnabled(
  hubspotApp: TeamApp | null | undefined,
): boolean {
  return Boolean(hubspotApp?.team_link_enabled);
}

export class ModalOpenBeforeLoadedError extends Error {
  name = 'ModalOpenBeforeLoadedError';
  message = 'Modal cannot be opened before the workspace is loaded';
}

export class LacksPermissionToManageHubspotError extends Error {
  name = 'LacksPermissionToManageHubspotError';
  message = 'User does not have permission to manage HubSpot';
}

export class UnreachableError extends Error {
  name = 'UnreachableError';
}
